import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import AppShell from '@/components/feature/AppShell';
import AuthGuard from '@/components/feature/AuthGuard';
import FeatureRoute from '@/components/feature/FeatureRoute';
import { runtimeConfig, type ApiMode } from '@/config/runtime';

import { ROUTES } from './paths';

const WordstatPage = lazy(() => import('../pages/wordstat/page'));
const WordstatTasksPage = lazy(() => import('../pages/wordstat/tasks-page'));
const WordstatTaskDetailPage = lazy(() => import('../pages/wordstat/task-detail'));
const WordstatNewPage = lazy(() => import('../pages/wordstat/new'));
const WordstatDetailPage = lazy(() => import('../pages/wordstat/detail'));

const NotFound = lazy(() => import('../pages/NotFound'));
const LoginPage = lazy(() => import('../pages/login/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const TargetsPage = lazy(() => import('../pages/targets/page'));
const BasketsPage = lazy(() => import('../pages/baskets/page'));
const BasketDetailPage = lazy(() => import('../pages/baskets/detail'));
const CredentialsPage = lazy(() => import('../pages/credentials/page'));
const ExperimentsPage = lazy(() => import('../pages/experiments/page'));
const ExperimentNewPage = lazy(() => import('../pages/experiments/new'));
const ExperimentDetailPage = lazy(() => import('../pages/experiments/detail'));
const CronsPage = lazy(() => import('../pages/crons/page'));
const CronNewPage = lazy(() => import('../pages/crons/new'));
const CronDetailPage = lazy(() => import('../pages/crons/detail'));
const AnalyticsPage = lazy(() => import('../pages/analytics/page'));
const SerpSetPage = lazy(() => import('../pages/serpset/page'));

export function createRoutes(apiMode: ApiMode): RouteObject[] {
  return [
    {
      path: ROUTES.login,
      element: (
        <FeatureRoute apiMode={apiMode} feature="authentication" standalone>
          <LoginPage />
        </FeatureRoute>
      ),
    },

    {
      element: (
        <AuthGuard>
          <AppShell apiMode={apiMode} />
        </AuthGuard>
      ),
      children: [
        { path: ROUTES.tasks.wordstat, element: <WordstatTasksPage /> },
        { path: ROUTES.tasks.wordstatNew, element: <WordstatNewPage /> },
        { path: ROUTES.tasks.wordstatDetailPattern, element: <WordstatTaskDetailPage /> },
        {
          path: ROUTES.analytics.wordstat,
          element: <WordstatPage />,
        },
        {
          path: ROUTES.analytics.wordstatDetailPattern,
          element: <WordstatDetailPage />,
        },
        {
          path: ROUTES.analytics.dynamics,
          element: (
            <FeatureRoute apiMode={apiMode} feature="analyticsDynamics">
              <AnalyticsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.analytics.serpset,
          element: (
            <FeatureRoute apiMode={apiMode} feature="serpResults">
              <SerpSetPage />
            </FeatureRoute>
          ),
        },

        {
          path: ROUTES.tasks.overview,
          element: (
            <FeatureRoute apiMode={apiMode} feature="taskDashboard">
              <DashboardPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.targets,
          element: (
            <FeatureRoute apiMode={apiMode} feature="searchTargets">
              <TargetsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.baskets,
          element: (
            <FeatureRoute apiMode={apiMode} feature="baskets">
              <BasketsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.basketDetailPattern,
          element: (
            <FeatureRoute apiMode={apiMode} feature="baskets">
              <BasketDetailPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.connections,
          element: (
            <FeatureRoute apiMode={apiMode} feature="credentials">
              <CredentialsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.oneTime,
          element: (
            <FeatureRoute apiMode={apiMode} feature="experiments">
              <ExperimentsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.oneTimeNew,
          element: (
            <FeatureRoute apiMode={apiMode} feature="experiments">
              <ExperimentNewPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.oneTimeDetailPattern,
          element: (
            <FeatureRoute apiMode={apiMode} feature="experiments">
              <ExperimentDetailPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.recurring,
          element: (
            <FeatureRoute apiMode={apiMode} feature="crons">
              <CronsPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.recurringNew,
          element: (
            <FeatureRoute apiMode={apiMode} feature="crons">
              <CronNewPage />
            </FeatureRoute>
          ),
        },
        {
          path: ROUTES.tasks.recurringDetailPattern,
          element: (
            <FeatureRoute apiMode={apiMode} feature="crons">
              <CronDetailPage />
            </FeatureRoute>
          ),
        },
      ],
    },

    { path: '/', element: <Navigate to={ROUTES.analytics.dynamics} replace /> },
    { path: '*', element: <NotFound /> },
  ];
}

const routes = createRoutes(runtimeConfig.apiMode);

export default routes;
